import React from "react";
import { dsyncStudioFaq } from "../../seo/pageMeta";
import "./DsyncStudioFeatures.css";

const MS_STORE_URL =
  "https://apps.microsoft.com/detail/9MZ09MB4D61Z?hl=en-us&gl=IN&ocid=pdpshare";

const AUDIENCE = [
  "Embedded Developers",
  "Firmware Engineers",
  "UI/UX Designers",
  "Automation Teams",
  "Product Developers",
];

const USE_CASES = [
  "Industrial Control Panels",
  "Embedded Product Interfaces",
  "Instrumentation Displays",
  "Automotive Interfaces",
  "Appliance Controllers",
  "Medical Equipment UIs",
];

const FEATURE_GROUPS = [
  {
    tag: "DESIGN",
    title: "Design Canvas & Widget Library",
    image: "/logo/dsync-feature-colorpicker.jpg",
    imageAlt: "Dsync Studio background color picker with RGB controls, gradients and presets",
    features: [
      {
        icon: "🎨",
        title: "Drag-and-Drop Canvas",
        desc: "Build screens visually with rulers, zoom, pan, smart guides, alignment assistance, grids, and multi-selection for precise, consistent layouts.",
      },
      {
        icon: "🧩",
        title: "Full Widget Library",
        desc: "Buttons, toggles & switches, sliders, knobs, gauges & dials, linear and circular progress bars, line charts, analog & digital clocks, numeric keypads, images, and shapes (square, circle, hexagon, triangle, line).",
      },
      {
        icon: "🎛️",
        title: "Per-Widget Property Panels",
        desc: "Every widget gets dedicated general, appearance, and data-format controls, plus a full RGB + gradient + preset color picker for any background or element.",
      },
      {
        icon: "🖼️",
        title: "Asset Management & PSD Import",
        desc: "Upload your own images and icons into a project, or bring in layered Photoshop (PSD) files directly onto the canvas.",
      },
    ],
  },
  {
    tag: "HARDWARE",
    title: "Live UART Connection & Flashing",
    image: "/logo/dsync-feature-canvas.jpg",
    imageAlt: "Dsync Studio canvas editor with a gauge, keypad, clock, toggles, knob and live chart",
    features: [
      {
        icon: "🔌",
        title: "Live UART Data Binding",
        desc: "Bind any widget property to a UART Data ID, with decimal/float formatting, so it updates live from real hardware or the built-in simulator.",
      },
      {
        icon: "🧪",
        title: "Built-in Data Simulator",
        desc: "Simulate incoming UART frames without hardware attached — assign matching Data IDs per chart line and preview behavior instantly.",
      },
      {
        icon: "⚡",
        title: "One-Click Firmware Flashing",
        desc: "Flash a finished design straight to the ESP32 over USB, with a live flash console, firmware version checks, and a guided update manager.",
      },
      {
        icon: "📡",
        title: "COM Port Tools & Debug Console",
        desc: "Select and refresh COM ports, watch a live UART monitor, and read the terminal and USB log while debugging on real hardware.",
      },
    ],
  },
  {
    tag: "PROJECTS",
    title: "Projects, Screens & Templates",
    image: "/logo/dsync-feature-templates.jpg",
    imageAlt: "Dsync Studio Template Library with Commercial, Industrial, Automotive and Medical categories",
    features: [
      {
        icon: "🖥️",
        title: "Multi-Screen Projects",
        desc: "Add as many screens as a project needs, and wire them together with \"Go to Screen\" navigation actions and menu layouts.",
      },
      {
        icon: "📚",
        title: "Template Library",
        desc: "Browse ready-made HMI projects filtered by Commercial, Industrial, Automotive, and Medical categories, resolution, and display size — open one in a click.",
      },
      {
        icon: "☁️",
        title: "Project Manager & Cloud Sync",
        desc: "Start a new project, open a .dsyncproj file, or jump back into recent work — sign in to keep projects synced across sessions.",
      },
      {
        icon: "📦",
        title: "Flexible Export",
        desc: "Package a finished design as a .dsync deployment file for the device, or export a PNG preview or background image.",
      },
    ],
  },
];

const HARDWARE_SPECS = [
  { label: "Display Sizes", value: "5\" and 7\"" },
  { label: "Resolution", value: "800 × 480" },
  { label: "Touch", value: "GT911 Capacitive, Ready" },
  { label: "Interface", value: "UART, easy integration" },
  { label: "Build", value: "Industrial grade, low power" },
];

const DsyncStudioFeatures = () => {
  return (
    <div className="dsf-page">
      <section className="dsf-hero">
        <div className="dsf-hero-text">
          <span className="dsf-eyebrow">ALL FEATURES</span>
          <h1 className="dsf-hero-title">
            Everything <span className="dsf-highlight">Dsync Studio</span> Can Do
          </h1>
          <p className="dsf-hero-sub">
            Dsync Studio is a professional Windows HMI design and development environment
            for BiBA's Smart UART displays. Design screens on a drag-and-drop canvas, wire
            them to live UART data, and flash the finished interface straight to hardware —
            all from one unified studio.
          </p>
          <div className="dsf-hero-buttons">
            <a href={MS_STORE_URL} target="_blank" rel="noopener noreferrer">
              <button className="dsf-btn dark">
                <img src="/logo/windows.png" alt="Windows" />
                Get it from Microsoft Store
              </button>
            </a>
          </div>
        </div>
        <div className="dsf-hero-picture">
          <img
            src="/logo/dsync-feature-canvas.jpg"
            alt="Dsync Studio canvas editor showing a speedometer gauge, numeric keypad, analog clock, toggles, knob and live line chart"
            className="dsf-hero-image"
          />
        </div>
      </section>

      <section className="dsf-audience">
        <div className="dsf-audience-block">
          <span className="dsf-audience-label">Built For</span>
          <div className="dsf-chip-row">
            {AUDIENCE.map((a) => (
              <span className="dsf-chip" key={a}>{a}</span>
            ))}
          </div>
        </div>
        <div className="dsf-audience-block">
          <span className="dsf-audience-label">Used Across</span>
          <div className="dsf-chip-row">
            {USE_CASES.map((u) => (
              <span className="dsf-chip alt" key={u}>{u}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="dsf-about">
        <h2 className="dsf-about-title">What Is Dsync Studio?</h2>
        <p className="dsf-about-text">
          Dsync Studio is BiBA Electronics' HMI design software for embedded engineers who
          need a working touchscreen interface without writing a graphics driver stack by
          hand. As a no-code UI design tool, it functions as a complete embedded GUI design
          tool: drag widgets onto a canvas, wire them to live UART data, and preview the
          result instantly, with no manual pixel-pushing in firmware.
        </p>
        <p className="dsf-about-text">
          Built specifically for BiBA's Smart UART HMI displays, Dsync Studio doubles as
          touchscreen HMI design software for microcontrollers and an industrial HMI
          software platform for teams shipping control panels, instrumentation displays and
          product interfaces. Whether you're new to embedded UI design or replacing a
          hand-rolled graphics stack, Dsync Studio simplifies how you design a GUI for
          embedded displays, from first sketch to flashed firmware.
        </p>
      </section>

      {FEATURE_GROUPS.map((group, i) => (
        <section
          className={`dsf-group ${i % 2 === 1 ? "reverse" : ""}`}
          key={group.title}
        >
          <div className="dsf-group-text">
            <span className="dsf-group-tag">{group.tag}</span>
            <h2 className="dsf-group-title">{group.title}</h2>
            <div className="dsf-feature-list">
              {group.features.map((f) => (
                <div className="dsf-feature" key={f.title}>
                  <span className="dsf-feature-icon">{f.icon}</span>
                  <div>
                    <h3 className="dsf-feature-title">{f.title}</h3>
                    <p className="dsf-feature-desc">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="dsf-group-picture">
            <img src={group.image} alt={group.imageAlt} className="dsf-group-image" />
          </div>
        </section>
      ))}

      <section className="dsf-hardware">
        <div className="dsf-hardware-text">
          <span className="dsf-group-tag">HARDWARE</span>
          <h2 className="dsf-group-title">Built for BiBA Smart UART Displays</h2>
          <p className="dsf-feature-desc">
            Dsync Studio targets BiBA's Smart UART HMI board family out of the box —
            pick a board from the Board Catalog to auto-configure resolution, flash
            package, and PWM resolution for the panel you're building for.
          </p>
          <div className="dsf-specs-grid">
            {HARDWARE_SPECS.map((s) => (
              <div className="dsf-spec" key={s.label}>
                <span className="dsf-spec-label">{s.label}</span>
                <span className="dsf-spec-value">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="dsf-hardware-picture">
          <img
            src="/logo/dsync-feature-dashboard.jpg"
            alt="Dsync Studio dashboard with workspace overview and project manager"
            className="dsf-hardware-image"
          />
        </div>
      </section>

      <section className="dsf-faq">
        <h2 className="dsf-group-title dsf-faq-title">Frequently Asked Questions</h2>
        <div className="dsf-faq-list">
          {dsyncStudioFaq.mainEntity.map((qa) => (
            <div className="dsf-faq-item" key={qa.name}>
              <h3 className="dsf-faq-question">{qa.name}</h3>
              <p className="dsf-faq-answer">{qa.acceptedAnswer.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="dsf-cta">
        <h2 className="dsf-cta-title">Ready to build your next embedded interface?</h2>
        <p className="dsf-cta-sub">
          Dsync Studio is free to download from the Microsoft Store.
        </p>
        <div className="dsf-hero-buttons dsf-cta-buttons">
          <a href={MS_STORE_URL} target="_blank" rel="noopener noreferrer">
            <button className="dsf-btn dark">
              <img src="/logo/windows.png" alt="Windows" />
              Get it from Microsoft Store
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default DsyncStudioFeatures;
